/**
 * The server's rendering of the template (D10).
 *
 * SERVER-AUTHORITATIVE, NOTHING RENDERED HERE. The subject, the HTML and the
 * plain text are exactly what `POST …/template/preview` returned; the client
 * has no template engine of its own, so the preview cannot drift from what the
 * server would render. The HTML is shown in a sandboxed frame (`sandbox=""`,
 * the rental-contract preview's pattern): no script runs, nothing reaches the
 * page. Nothing is sent — a preview stores and mails nothing.
 *
 * CURRENT OR NOT, AND SAYING WHICH. After an edit the last rendering no longer
 * shows the draft; it stays visible, marked "nicht aktuell", until the reader
 * asks for a new one. No request per keystroke.
 *
 * EXAMPLE DATA, NAMED AS SUCH. The values a preview uses are the catalogue's
 * fixed fictional examples; they are listed under the rendering so nobody
 * mistakes it for a real letter. The reminder's system letter exists in fixed
 * variants, and the reader chooses which one to see.
 */
import React, { useState } from "react";
import { RefreshCw } from "lucide-react";

import {
  placeholderLabel,
  previewWarningLabel,
  scenarioLabel,
} from "./automationTemplateLabels";

const BUTTON =
  "inline-flex items-center gap-1.5 rounded-[8px] border border-border bg-card px-2.5 py-1 text-[12px] font-medium text-foreground hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50 max-md:min-h-9";

const VIEW_BUTTON =
  "rounded-[7px] px-2.5 py-1 text-[12px] font-medium text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring aria-pressed:bg-card aria-pressed:text-foreground aria-pressed:shadow-sm max-md:min-h-9";

export default function AutomationTemplatePreview({
  preview,
  isCurrent,
  basisLabel,
  onRefresh,
  refreshDisabled = false,
  scenarios = [],
  scenarioKey = null,
  onScenarioChange,
  scenarioEnabled = false,
  placeholders = [],
}) {
  const [view, setView] = useState("html");
  const result = preview.result;
  const loading = preview.status === "loading";
  const failed = preview.status === "error";
  const stale = !loading && !isCurrent && (result || failed);

  return (
    <section
      aria-label="Vorschau"
      className="flex min-w-0 flex-col gap-3 rounded-[14px] border border-border bg-card p-3.5"
    >
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex min-w-0 flex-col">
          <h3 className="m-0 text-[13.5px] font-semibold text-foreground">Vorschau</h3>
          <p className="m-0 text-[11.5px] text-muted-foreground">
            {basisLabel} · mit Beispieldaten, es wird nichts versendet
          </p>
        </div>
        <button
          type="button"
          onClick={onRefresh}
          disabled={refreshDisabled || loading}
          className={BUTTON}
        >
          <RefreshCw className="h-3.5 w-3.5" aria-hidden="true" />
          Vorschau aktualisieren
        </button>
      </div>

      {scenarios.length ? (
        <label className="flex flex-col gap-1 text-[11.5px] font-medium text-muted-foreground">
          Beispiel
          <select
            value={scenarioKey ?? ""}
            disabled={!scenarioEnabled || loading}
            onChange={(event) => onScenarioChange(event.target.value)}
            className="rounded-[8px] border border-border bg-background px-2 py-1.5 text-[12.5px] text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:opacity-60 max-md:min-h-9"
          >
            {scenarios.map((scenario) => (
              <option key={scenario.key} value={scenario.key}>
                {scenarioLabel(scenario)}
              </option>
            ))}
          </select>
        </label>
      ) : null}

      {loading ? (
        <p role="status" className="m-0 text-[12.5px] text-muted-foreground">
          Vorschau wird erstellt …
        </p>
      ) : null}

      {stale ? (
        <p
          role="status"
          className="m-0 rounded-[10px] border border-border bg-muted/50 px-3 py-2 text-[12px] text-foreground"
        >
          Vorschau nicht aktuell — sie zeigt nicht den aktuellen Stand der Bearbeitung.
        </p>
      ) : null}

      {failed && isCurrent ? (
        <div className="flex flex-col items-start gap-2">
          <p role="alert" className="m-0 text-[12.5px] text-destructive">
            {preview.error?.message}
          </p>
          <button type="button" onClick={onRefresh} className={BUTTON}>
            Erneut versuchen
          </button>
        </div>
      ) : null}

      {result ? (
        <div className={`flex min-w-0 flex-col gap-2 ${stale || loading ? "opacity-60" : ""}`}>
          <dl className="m-0 flex flex-col gap-0.5">
            <dt className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
              Betreff
            </dt>
            <dd className="m-0 break-words text-[13px] font-semibold text-foreground">
              {result.rendered_subject}
            </dd>
          </dl>
          <div
            role="group"
            aria-label="Darstellung"
            className="inline-flex self-start rounded-[9px] border border-border bg-muted/40 p-0.5"
          >
            <button
              type="button"
              aria-pressed={view === "html"}
              onClick={() => setView("html")}
              className={VIEW_BUTTON}
            >
              E-Mail
            </button>
            <button
              type="button"
              aria-pressed={view === "text"}
              onClick={() => setView("text")}
              className={VIEW_BUTTON}
            >
              Nur Text
            </button>
          </div>
          {view === "html" ? (
            <iframe
              title="Vorschau der E-Mail"
              srcDoc={result.rendered_html}
              sandbox=""
              className="h-[420px] w-full rounded-[10px] border border-border bg-background"
            />
          ) : (
            <pre
              aria-label="Vorschau als Text"
              className="m-0 max-h-[420px] overflow-auto whitespace-pre-wrap break-words rounded-[10px] border border-border bg-background p-3 font-sans text-[12.5px] leading-relaxed text-foreground"
            >
              {result.rendered_text}
            </pre>
          )}
          {Array.isArray(result.warnings) && result.warnings.length ? (
            <ul aria-label="Hinweise zur Vorschau" className="m-0 flex flex-col gap-1 pl-4 text-[12px] text-muted-foreground">
              {result.warnings.map((warning, index) => (
                <li key={`${warning.code}-${warning.key}-${index}`}>
                  {previewWarningLabel(warning)}
                </li>
              ))}
            </ul>
          ) : null}
        </div>
      ) : null}

      {placeholders.length ? (
        <details className="text-[12px] text-muted-foreground">
          <summary className="cursor-pointer select-none font-medium text-foreground">
            Beispielwerte
          </summary>
          <dl className="m-0 mt-2 grid grid-cols-[minmax(0,auto)_minmax(0,1fr)] gap-x-3 gap-y-1">
            {placeholders.map((placeholder) => (
              <React.Fragment key={placeholder.key}>
                <dt className="text-muted-foreground">{placeholderLabel(placeholder.key)}</dt>
                <dd className="m-0 break-words text-foreground">{placeholder.example_value}</dd>
              </React.Fragment>
            ))}
          </dl>
        </details>
      ) : null}
    </section>
  );
}
